import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Cookies from "js-cookie";
import { GUEST_TOKEN, TOKEN_COOKIE } from "./services/constants";
import Login from "./pages/Login/Login";
import Dashboard from "./pages/Dashboard/Dashboard";
import Bugs from "./pages/Bugs/Bugs";
import Profile from "./pages/Profile/Profile";
import AddProject from "./pages/AddProject/AddProject";
import Register from "./pages/Register/Register";
import BugDetail from "./pages/BugDetail/BugDetail";
import "./App.css";
import InvitePage from "./pages/Invite/Invite";
import PrivacyPolicy from "./pages/Privacy/Privacy";
import Settings from "./pages/Settings/Settings";
import ResetPassword from "./pages/ResetPassword/ResetPassword";

const THEME_KEY = "buglog-theme";
const THEME_COLORS = {
  light: "#489BF4",
  dark: "#0d1117",
};

const setThemeMetaColor = (theme) => {
  try {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", THEME_COLORS[theme] || THEME_COLORS.light);
  } catch {
  }
};

const applyDocumentTheme = (theme) => {
  const next = theme === "dark" ? "dark" : "light";
  document.documentElement.classList.toggle("dark", next === "dark");
  document.documentElement.dataset.theme = next;
  setThemeMetaColor(next);
  try {
    window.dispatchEvent(new CustomEvent("buglog:themechange", { detail: { theme: next } }));
  } catch {
  }
};

function App() {
  const token = Cookies.get(TOKEN_COOKIE);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");

    const readStoredTheme = () => {
      try {
        const stored = localStorage.getItem(THEME_KEY);
        return stored === "dark" || stored === "light" ? stored : "";
      } catch {
        return "";
      }
    };

    const resolveTheme = () => readStoredTheme() || (mq.matches ? "dark" : "light");

    applyDocumentTheme(resolveTheme());

    const onSystemChange = (e) => {
      if (!readStoredTheme()) {
        applyDocumentTheme(e.matches ? "dark" : "light");
      }
    };

    const onStorage = (e) => {
      if (e.key === THEME_KEY) {
        applyDocumentTheme(resolveTheme());
      }
    };

    mq.addEventListener("change", onSystemChange);
    window.addEventListener("storage", onStorage);

    return () => {
      mq.removeEventListener("change", onSystemChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            token ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/dashboard"
          element={
            token === GUEST_TOKEN ? (
              <Navigate to="/login" />
            ) : token ? (
              <Dashboard />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/companies"
          element={
            token === GUEST_TOKEN ? (
              <Navigate to="/login" />
            ) : token ? (
              <Dashboard />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/companies/:companyName"
          element={
            token === GUEST_TOKEN ? (
              <Navigate to="/login" />
            ) : token ? (
              <Dashboard />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/login"
          element={
            token === GUEST_TOKEN ? (
              <Login />
            ) : token ? (
              <Navigate to="/dashboard" />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path="/project"
          element={
            token === GUEST_TOKEN ? (
              <Navigate to="/login" />
            ) : token ? (
              <Navigate to={`/project?token=${token}`} />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/bugs/:projectId/:isOwner/:projectName"
          element={token ? <Bugs /> : <Navigate to="/" />}
        />
        <Route path="/invite/:uniqid" element={<InvitePage />} />
        <Route path="/invite/:uniqid/:title" element={<InvitePage />} />
        <Route
          path="/profile"
          element={
            token === GUEST_TOKEN ? (
              <Navigate to="/login" />
            ) : token ? (
              <Profile />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/add-project"
          element={
            token === GUEST_TOKEN ? (
              <Navigate to="/login" />
            ) : token ? (
              <AddProject />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/add-company"
          element={
            token === GUEST_TOKEN ? (
              <Navigate to="/login" />
            ) : token ? (
              <AddProject mode="company" />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/add-company/:companyName/project"
          element={
            token === GUEST_TOKEN ? (
              <Navigate to="/login" />
            ) : token ? (
              <AddProject mode="companyProject" />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/register"
          element={
            token === GUEST_TOKEN ? (
              <Register />
            ) : token ? (
              <Navigate to="/dashboard" />
            ) : (
              <Register />
            )
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/bug_detail/:bugId"
          element={token ? <BugDetail /> : <Navigate to="/" />}
        />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route
          path="/settings"
          element={
            token === GUEST_TOKEN ? (
              <Navigate to="/login" />
            ) : token ? (
              <Settings />
            ) : (
              <Navigate to="/" />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
